import React, { useEffect, useMemo, useRef, useState } from "react";
import "./doctors-admin.css";
import { API_URL, BASE_URL } from "../../config";

const MAX_IMAGES = 3;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB

const emptyDoctor = {
    _id: null,
    name_ru: "", name_uz: "",
    department_ru: "", department_uz: "",
    experience_ru: "", experience_uz: "",
    description_ru: "", description_uz: "",
    images: [], // existing images URLs
    newFiles: [] // new File objects to upload
};

const DoctorsAdmin = () => {
    const [list, setList] = useState([]);
    const [form, setForm] = useState(emptyDoctor);
    const [errors, setErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [langTab, setLangTab] = useState('ru'); 
    const fileRef = useRef(null);

    const loadDoctors = async () => {
        try {
            const res = await fetch(`${API_URL}/doctors`);
            const data = await res.json();
            setList(data);
        } catch (err) {
            console.error("Ошибка загрузки врачей: ", err);
        }
    };

    useEffect(() => {
        loadDoctors();
    }, []);

    const remainingImages = useMemo(
        () => MAX_IMAGES - (form.images.length + form.newFiles.length),
        [form.images, form.newFiles]
    );

    const onPickImages = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const currentTotal = form.images.length + form.newFiles.length;
        const room = Math.max(0, MAX_IMAGES - currentTotal);
        const slice = files.slice(0, room);

        const errs = {};
        const validFiles = [];

        for (const f of slice) {
            if (!f.type.startsWith("image/")) {
                errs.images = "Можно добавлять только изображения.";
                continue;
            }
            if (f.size > MAX_IMAGE_BYTES) {
                errs.images = `Размер каждого изображения ≤ 5MB. Файл ${f.name} больше лимита.`;
                continue;
            }
            validFiles.push(f);
        }

        setErrors((p) => ({ ...p, ...errs }));
        if (validFiles.length) {
            setForm((p) => ({ ...p, newFiles: [...p.newFiles, ...validFiles] }));
        }
        if (fileRef.current) fileRef.current.value = "";
    };

    const removeExistingImage = (idx) => {
        setForm((p) => ({
            ...p,
            images: p.images.filter((_, i) => i !== idx),
        }));
    };

    const removeNewFile = (idx) => {
        setForm((p) => ({
            ...p,
            newFiles: p.newFiles.filter((_, i) => i !== idx),
        }));
    };

    const startEdit = (doc) => {
        setIsEditing(true);
        setForm({
            _id: doc._id,
            name_ru: doc.name_ru || "", name_uz: doc.name_uz || "",
            department_ru: doc.department_ru || "", department_uz: doc.department_uz || "",
            experience_ru: doc.experience_ru || "", experience_uz: doc.experience_uz || "",
            description_ru: doc.description_ru || "", description_uz: doc.description_uz || "",
            images: doc.images || [],
            newFiles: []
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const resetForm = () => {
        setForm(emptyDoctor);
        setIsEditing(false);
        setErrors({});
        if (fileRef.current) fileRef.current.value = "";
    };

    const validate = () => {
        const e = {};
        if (!form.name_ru.trim() && !form.name_uz.trim()) e.name = "Укажите имя хотя бы на одном языке";
        if (!form.department_ru.trim() && !form.department_uz.trim()) e.department = "Укажите специальность хотя бы на одном языке";
        if (!form.description_ru.trim() && !form.description_uz.trim()) e.description = "Добавьте описание хотя бы на одном языке";
        if (form.images.length === 0 && form.newFiles.length === 0)
            e.images = "Добавьте хотя бы одно фото";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const saveDoctor = async () => {
        if (!validate()) return;
        
        const fd = new FormData();
        fd.append('name_ru', form.name_ru);
        fd.append('name_uz', form.name_uz);
        fd.append('department_ru', form.department_ru);
        fd.append('department_uz', form.department_uz);
        fd.append('experience_ru', form.experience_ru);
        fd.append('experience_uz', form.experience_uz);
        fd.append('description_ru', form.description_ru);
        fd.append('description_uz', form.description_uz);
        
        if (isEditing) {
            fd.append('existingImages', JSON.stringify(form.images));
            form.newFiles.forEach(f => fd.append('newImages', f));
            
            try {
                const res = await fetch(`${API_URL}/doctors/${form._id}`, {
                    method: 'PUT',
                    body: fd
                });
                if (res.ok) {
                    await loadDoctors();
                    resetForm();
                } else {
                    alert("Ошибка сохранения");
                }
            } catch(e) { alert(e.message) }
        } else {
            form.newFiles.forEach(f => fd.append('images', f));
            try {
                const res = await fetch(`${API_URL}/doctors`, {
                    method: 'POST',
                    body: fd
                });
                if (res.ok) {
                    await loadDoctors();
                    resetForm();
                } else {
                    const errText = await res.text();
                    alert("Ошибка добавления: " + errText);
                }
            } catch(e) { alert("Catch error: " + e.message) }
        }
    };

    const deleteDoctor = async (id) => {
        if (!window.confirm("Удалить эту карточку врача?")) return;
        try {
            const res = await fetch(`${API_URL}/doctors/${id}`, { method: 'DELETE' });
            if (res.ok) {
                await loadDoctors();
                if (form._id === id) resetForm();
            }
        } catch (e) {
            alert(e.message);
        }
    };

    return (
        <div className="doctors-admin">
            <div className="header">
                <h2>Врачи</h2>
                <p className="muted">Добавляйте, редактируйте и удаляйте карточки. До 3 фото (каждое ≤ 5MB).</p>
            </div>

            <div className="card">
                <div className="card-head">
                    <h3>{isEditing ? "Редактировать врача" : "Добавить врача"}</h3>
                </div>

                <div style={{display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px'}}>
                    <button 
                        className={`btn ${langTab === 'ru' ? 'btn-primary' : 'btn-outline'}`} 
                        onClick={() => setLangTab('ru')}
                    >
                        Русский
                    </button>
                    <button 
                        className={`btn ${langTab === 'uz' ? 'btn-primary' : 'btn-outline'}`} 
                        onClick={() => setLangTab('uz')}
                    >
                        O'zbekcha
                    </button>
                </div>

                <div className="form-grid">
                    <div className="field">
                        <label>Имя ({langTab.toUpperCase()})*</label>
                        <input
                            value={langTab === 'ru' ? form.name_ru : form.name_uz}
                            onChange={(e) => setForm({ ...form, [langTab === 'ru' ? 'name_ru' : 'name_uz']: e.target.value })}
                            placeholder="Например: Новикова Екатерина"
                        />
                        {errors.name && <div className="error">{errors.name}</div>}
                    </div>

                    <div className="field">
                        <label>Специальность ({langTab.toUpperCase()})*</label>
                        <input
                            value={langTab === 'ru' ? form.department_ru : form.department_uz}
                            onChange={(e) => setForm({ ...form, [langTab === 'ru' ? 'department_ru' : 'department_uz']: e.target.value })}
                            placeholder="кардиолог / кардиохирург"
                        />
                        {errors.department && <div className="error">{errors.department}</div>}
                    </div>

                    <div className="field">
                        <label>Опыт ({langTab.toUpperCase()})</label>
                        <input
                            value={langTab === 'ru' ? form.experience_ru : form.experience_uz}
                            onChange={(e) => setForm({ ...form, [langTab === 'ru' ? 'experience_ru' : 'experience_uz']: e.target.value })}
                            placeholder="например: 15 лет"
                        />
                    </div>

                    <div className="field field-col">
                        <label>Описание ({langTab.toUpperCase()})*</label>
                        <textarea
                            rows={4}
                            value={langTab === 'ru' ? form.description_ru : form.description_uz}
                            onChange={(e) => setForm({ ...form, [langTab === 'ru' ? 'description_ru' : 'description_uz']: e.target.value })}
                            placeholder="Коротко об опыте и регалиях"
                        />
                        {errors.description && <div className="error">{errors.description}</div>}
                    </div>

                    <div className="field field-col">
                        <label>Фотографии (до 3 шт., ≤ 5MB)</label>
                        <div className="uploader">
                            <input
                                ref={fileRef}
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={onPickImages}
                            />
                            <div className="uploader-hint">
                                Выберите файлы (можно добавить ещё {remainingImages})
                            </div>
                        </div>
                        {errors.images && <div className="error">{errors.images}</div>}

                        <div className="images-grid">
                            {form.images.map((url, i) => (
                                <div className="thumb" key={`ex-${i}`}>
                                    <img src={`${BASE_URL}${url}`} alt={`existing-${i}`} />
                                    <button type="button" className="btn btn-danger btn-small" onClick={() => removeExistingImage(i)}>
                                        Удалить
                                    </button>
                                </div>
                            ))}
                            {form.newFiles.map((file, i) => (
                                <div className="thumb" key={`new-${i}`}>
                                    <img src={URL.createObjectURL(file)} alt={`new-${i}`} />
                                    <button type="button" className="btn btn-danger btn-small" onClick={() => removeNewFile(i)}>
                                        Удалить
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="actions">
                        <button type="button" className="btn btn-primary" onClick={saveDoctor}>
                            {isEditing ? "Сохранить" : "Добавить"}
                        </button>
                        {isEditing && (
                            <button type="button" className="btn btn-link" onClick={resetForm}>
                                Отмена
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="list">
                {list.length === 0 && <div className="muted">Список пуст. Добавьте первую карточку врача.</div>}

                {list.map((d) => (
                    <DoctorListCard key={d._id} doc={d} onEdit={() => startEdit(d)} onDelete={() => deleteDoctor(d._id)} />
                ))}
            </div>
        </div>
    );
};

function DoctorListCard({ doc, onEdit, onDelete }) {
    return (
        <div className="doctor-card card">
            <div className="doctor-card-head">
                <div className="title">
                    <div className="name">{doc.name_ru || doc.name}</div>
                    <div className="spec">{doc.department_ru || doc.department}</div>
                    {doc.experience_ru && <div className="exp">Опыт: {doc.experience_ru}</div>}
                    <div className="name" style={{fontSize: '0.9em', color: '#666', marginTop: '5px'}}>UZ: {doc.name_uz}</div>
                </div>
                <div className="head-actions">
                    <button className="btn btn-outline" onClick={onEdit}>Редактировать</button>
                    <button className="btn btn-danger" onClick={onDelete}>Удалить</button>
                </div>
            </div>
            <div className="desc">{doc.description_ru || doc.description}</div>
            <div className="thumbs-row">
                {(doc.images || []).map((url, i) => (
                    <img key={i} src={`${BASE_URL}${url}`} alt={`${doc.name_ru}-${i}`} />
                ))}
            </div>
        </div>
    );
}

export default DoctorsAdmin;
